import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  type Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  DEPOSIT_BOOKING_CONTEXTS,
  DEPOSIT_BOOKING_CONTEXT_LABELS,
  DEPOSIT_CAPTURE_STRATEGY_TEXT,
  DepositCaptureStrategy,
  type IDepositOptionText,
} from '@principle-theorem/principle-core/interfaces';
import { DAYS_OF_WEEK } from '@principle-theorem/shared';
import { startWith, switchMap } from 'rxjs/operators';
import { DepositTreatmentTemplateFormGroup } from '../deposit-treatment-template.formgroup';
import { DEPOSIT_TIMING_QUESTION } from './deposit-vocabulary';

interface IDepositCaptureStrategyOption extends IDepositOptionText {
  readonly value: DepositCaptureStrategy;
}

const depositAmountStepDollars = 0.01;

/**
 * The deposit configuration controls for a single treatment template.
 *
 * This exists once and is rendered by both surfaces that configure a
 * template's deposit — the Deposit Settings template dialog and the Treatment
 * Templates upsert dialog. They previously carried separate copies of this
 * markup, which is how they came to enforce opposite validity rules for one
 * persisted field and to describe one concept with two vocabularies.
 *
 * It deliberately does NOT render the `depositRequired` toggle or any dialog
 * chrome: each host words and places that toggle differently, and that is a
 * genuine difference rather than drift.
 */
@Component({
  selector: 'pr-deposit-configuration-form',
  templateUrl: './deposit-configuration-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class DepositConfigurationFormComponent {
  readonly form = input.required<DepositTreatmentTemplateFormGroup>();
  /**
   * Prefixes every accessible name so multiple instances on one screen stay
   * distinguishable — the template name at brand level, a fixed noun elsewhere.
   */
  readonly ariaLabelPrefix = input('');

  readonly timingQuestion = DEPOSIT_TIMING_QUESTION;
  readonly bookingContexts = DEPOSIT_BOOKING_CONTEXTS;
  readonly bookingContextLabels = DEPOSIT_BOOKING_CONTEXT_LABELS;
  readonly depositRequiredDays = DAYS_OF_WEEK;
  readonly amountStep = depositAmountStepDollars;
  readonly inheritedCaptureStrategyLabel: Signal<string | undefined>;

  readonly captureStrategyOptions: IDepositCaptureStrategyOption[] = [
    {
      value: DepositCaptureStrategy.ChargeOnBookingRequest,
      ...DEPOSIT_CAPTURE_STRATEGY_TEXT[
        DepositCaptureStrategy.ChargeOnBookingRequest
      ],
    },
    {
      value: DepositCaptureStrategy.CaptureOnBookingRequestApproval,
      ...DEPOSIT_CAPTURE_STRATEGY_TEXT[
        DepositCaptureStrategy.CaptureOnBookingRequestApproval
      ],
    },
    {
      value: DepositCaptureStrategy.CaptureOnCancellationOrReschedule,
      ...DEPOSIT_CAPTURE_STRATEGY_TEXT[
        DepositCaptureStrategy.CaptureOnCancellationOrReschedule
      ],
    },
  ];

  constructor() {
    const captureStrategy = toSignal(
      toObservable(this.form).pipe(
        switchMap((form) =>
          form.controls.captureStrategy.valueChanges.pipe(
            startWith(form.controls.captureStrategy.value)
          )
        )
      )
    );

    /**
     * The brand default this template is still following, when it has not chosen
     * its own. Undefined once the user overrides it, so the select stops
     * advertising an inheritance that no longer applies.
     */
    this.inheritedCaptureStrategyLabel = computed(() => {
      const form = this.form();
      const strategy = captureStrategy();
      if (!form.inheritsCaptureStrategy || !strategy) {
        return;
      }
      return DEPOSIT_CAPTURE_STRATEGY_TEXT[strategy].label;
    });
  }
}
